import { useState, useEffect } from 'react';
import * as DateFns from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';

import { Dropdown } from '../../containers';
import { Input } from './Input';
import { Icon } from '../../ui_elements/icons';
import { CustomRangeSelector } from './CustomRangeSelector';

import { fonts, zindex, colors } from '../../styles/theme';

const CalendarIcon = styled(Icon)`
  position: absolute;
  width: 40px;
  line-height: 40px;
  left: 0;
  top: 0;
  z-index: ${zindex.component.aboveBase};
  color: ${colors.blue.primaryDark};
`;

const DropdownArrow = styled(Icon)`
  font-size: ${fonts.sans.size.large};
  width: 20px;
  position: absolute;
  line-height: 40px;
  right: 10px;
  top: 0;
  pointer-events: none;
`;

const RangeSelect = styled(Input)`
  padding: 7px 8px 7px 35px;
`;

const DateRangePicker = (props) => {
  const { onDatesChange, defaultDateRange } = props;
  const [customRangeSelected, setCustomRangeSelected] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(defaultDateRange);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();

  const today = new Date();

  const setDates = (range, startDate, endDate) => {
    setSelectedDateRange(range);
    setFromDate(startDate);
    setToDate(endDate);
    onDatesChange(startDate, endDate);
  };

  const [dateRangeLinks] = useState([
    {
      name: 'Last 7 days',
      action: () => {
        setDates('Last 7 days', DateFns.startOfDay(DateFns.subDays(today, 7)), today);
      },
    },
    {
      name: 'Last 30 days',
      action: () => {
        setDates('Last 30 days', DateFns.startOfDay(DateFns.subDays(today, 30)), today);
      },
    },
    {
      name: 'Last 90 days',
      action: () => {
        setDates('Last 90 days', DateFns.startOfDay(DateFns.subDays(today, 90)), today);
      },
    },
    {
      name: 'Month-to-date',
      action: () => {
        setDates('Month-to-date', DateFns.startOfMonth(today), today);
      },
    },
    {
      name: 'Last Month',
      action: () => {
        setDates(
          'Last Month',
          DateFns.startOfMonth(DateFns.subMonths(today, 1)),
          DateFns.endOfMonth(DateFns.subMonths(today, 1))
        );
      },
    },
    {
      name: 'Last Quarter',
      action: () => {
        setDates(
          'Last Quarter',
          DateFns.startOfQuarter(DateFns.subQuarters(today, 1)),
          DateFns.endOfQuarter(DateFns.subQuarters(today, 1))
        );
      },
    },
    {
      name: 'Select date range',
      action: () => setCustomRangeSelected(true),
    },
  ]);

  useEffect(() => {
    dateRangeLinks.find((link) => link.name === defaultDateRange).action();
  }, [dateRangeLinks, defaultDateRange]);

  const TriggerInput = () => {
    return (
      <>
        <CalendarIcon type="calendar" />
        <RangeSelect defaultValue={selectedDateRange} />
        <DropdownArrow type="angle-down" />
      </>
    );
  };

  let dateRangeSelector = <Dropdown links={dateRangeLinks} Trigger={TriggerInput} />;

  const handleRangeSelected = (startDate, endDate) => {
    setCustomRangeSelected(false);
    setDates('Custom range selected', startDate, endDate);
  };

  if (customRangeSelected) {
    dateRangeSelector = (
      <>
        <CustomRangeSelector
          onRangeSelected={handleRangeSelected}
          defaultFromDate={fromDate}
          defaultToDate={toDate}
        />
        <Icon type="x" onClick={() => setCustomRangeSelected(false)} />
      </>
    );
  }

  return <>{dateRangeSelector}</>;
};

DateRangePicker.propTypes = {
  onDatesChange: PropTypes.func.isRequired,
  defaultDateRange: PropTypes.string,
};

DateRangePicker.defaultProps = {
  defaultDateRange: 'Last 7 days',
};

export default DateRangePicker;
